/**
 * ============================================================
 *  新增文件 -- 文章搜索 (blog-search.js)
 * ============================================================
 *
 * <blog-search>
 * 单 blog 内的客户端文章搜索：用 Liquid 内联的 JSON 索引，按
 * title / excerpt / tags 做大小写不敏感的多词 AND 匹配。
 *
 * 零依赖（Web Components + 原生 DOM）。100 篇 / 仅元数据规模下，
 * 单次过滤耗时远小于 1 ms，移动端也无感。
 *
 * 与 blog-tags.js 协作：监听 `blog:content-updated` 事件，在
 * 标签 AJAX 切换后自动重建 DOM 引用并按当前关键词重跑。
 */
if (!customElements.get("blog-search")) {
  customElements.define(
    "blog-search",
    class BlogSearch extends HTMLElement {
      constructor() {
        super();
        this.debounceTimer = null;
        this.lastTerm = "";
        this.lastSort = "";
        this.currentPage = 1;
      }

      connectedCallback() {
        this.input = this.querySelector("[data-blog-search-input]");
        this.clearBtn = this.querySelector("[data-blog-search-clear]");
        this.sortSelect = this.querySelector("[data-blog-sort-select]");
        this.sortControl = this.querySelector("[data-blog-sort-control]");
        this.sortTrigger = this.querySelector("[data-blog-sort-trigger]");
        this.sortMenu = this.querySelector("[data-blog-sort-menu]");
        this.sortValue = this.querySelector("[data-blog-sort-value]");
        this.sortOptions = Array.from(
          this.querySelectorAll("[data-blog-sort-option]"),
        );

        if (!this.input && !this.sortSelect) return;

        this.showDate = this.dataset.showDate === "true";
        this.showAuthor = this.dataset.showAuthor === "true";
        this.showTags = this.dataset.showTags === "true";
        this.showExcerpt = this.dataset.showExcerpt === "true";
        this.ratioPercent = this.computeRatio(this.dataset.imageRatio);
        this.pageSize = Math.max(
          1,
          Number.parseInt(this.dataset.articlesPerPage || "10", 10) || 10,
        );

        this.refreshContentRefs();
        this.bindEvents();
        this.syncFromQuery();
      }

      /**
       * Re-resolve all DOM references that live inside #Blog-Content.
       * Called on first init and again every time blog-tags.js replaces
       * the content via AJAX (tag filter switch).
       */
      refreshContentRefs() {
        this.defaultView = this.querySelector("[data-blog-search-default]");
        this.resultsWrap = this.querySelector("[data-blog-search-results]");
        this.resultsList = this.querySelector("[data-blog-search-results-list]");
        this.emptyMsg = this.querySelector("[data-blog-search-empty]");
        this.pagination = this.querySelector("[data-blog-search-pagination]");
        this.statusEl = this.querySelector("[data-blog-search-status]");
        this.dataScript = this.querySelector("[data-blog-search-data]");
        this.parseIndex();
      }

      parseIndex() {
        if (!this.dataScript) {
          this.articles = [];
          return;
        }
        try {
          const raw = JSON.parse(this.dataScript.textContent || "[]");
          this.articles = raw.map((a) => ({
            ...a,
            _haystack: [
              a.title || "",
              a.excerpt || "",
              Array.isArray(a.tags) ? a.tags.join(" ") : "",
            ]
              .join(" \u0001 ")
              .toLowerCase(),
          }));
        } catch (e) {
          this.articles = [];
          console.warn("[blog-search] failed to parse article index", e);
        }
      }

      bindEvents() {
        if (this.input) {
          this.input.addEventListener("input", () => {
            this.currentPage = 1;
            window.clearTimeout(this.debounceTimer);
            this.debounceTimer = window.setTimeout(() => this.run(), 150);
          });

          this.input.addEventListener("search", () => {
            this.currentPage = 1;
            this.run();
          });
        }

        if (this.clearBtn) {
          this.clearBtn.addEventListener("click", () => {
            this.input.value = "";
            this.currentPage = 1;
            this.run();
            this.input.focus();
          });
        }

        if (this.sortSelect) {
          this.sortSelect.addEventListener("change", () => {
            this.syncSortUi();
            this.currentPage = 1;
            this.run({ force: true });
          });
        }
        this.bindSortMenu();

        // Sync ?bq=..., ?sort=... and ?bpage=... back/forward navigation.
        window.addEventListener("popstate", () => this.syncFromQuery());

        // blog-tags.js AJAX-swaps #Blog-Content when the user picks a tag.
        // All our content-side refs (JSON script, results container, status
        // text, default paginated view) get detached and replaced. We need
        // to re-resolve them and re-apply any active query against the new
        // (tag-filtered) dataset.
        document.addEventListener("blog:content-updated", () => {
          this.refreshContentRefs();
          this.lastTerm = "";
          this.lastSort = "";
          this.currentPage = 1;
          this.run({ skipUrl: true, force: true });
        });
      }

      syncFromQuery() {
        const params = new URLSearchParams(window.location.search);
        const term = params.get("bq") || "";
        const requestedSort = params.get("sort") || "newest";
        const sort = this.isValidSort(requestedSort) ? requestedSort : "newest";
        const requestedPage = Number.parseInt(params.get("bpage") || "1", 10);

        if (this.input && term !== this.input.value) {
          this.input.value = term;
        }
        if (this.sortSelect) {
          this.sortSelect.value = sort;
          this.syncSortUi();
        }
        this.currentPage =
          Number.isFinite(requestedPage) && requestedPage > 0
            ? requestedPage
            : 1;
        this.run({ skipUrl: true, force: true });
      }

      bindSortMenu() {
        if (
          !this.sortControl ||
          !this.sortTrigger ||
          !this.sortMenu ||
          !this.sortOptions.length
        ) {
          return;
        }

        this.sortTrigger.addEventListener("click", () => {
          if (this.sortControl.classList.contains("is-open")) {
            this.closeSortMenu();
          } else {
            this.openSortMenu();
          }
        });

        this.sortTrigger.addEventListener("keydown", (event) => {
          if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;
          event.preventDefault();
          this.openSortMenu(event.key === "ArrowUp" ? "last" : "selected");
        });

        this.sortMenu.addEventListener("click", (event) => {
          const option = event.target.closest("[data-blog-sort-option]");
          if (option) this.chooseSortOption(option);
        });

        this.sortMenu.addEventListener("keydown", (event) => {
          const currentIndex = this.sortOptions.indexOf(
            document.activeElement,
          );
          let nextIndex = currentIndex;

          switch (event.key) {
            case "ArrowDown":
              nextIndex = (currentIndex + 1) % this.sortOptions.length;
              break;
            case "ArrowUp":
              nextIndex =
                (currentIndex - 1 + this.sortOptions.length) %
                this.sortOptions.length;
              break;
            case "Home":
              nextIndex = 0;
              break;
            case "End":
              nextIndex = this.sortOptions.length - 1;
              break;
            case "Escape":
              event.preventDefault();
              this.closeSortMenu({ focusTrigger: true });
              return;
            case "Tab":
              this.closeSortMenu();
              return;
            default:
              return;
          }

          event.preventDefault();
          this.sortOptions[nextIndex]?.focus();
        });

        document.addEventListener("click", (event) => {
          if (!this.sortControl.contains(event.target)) this.closeSortMenu();
        });

        this.syncSortUi();
      }

      openSortMenu(focusTarget = "selected") {
        this.sortControl.classList.add("is-open");
        this.sortTrigger.setAttribute("aria-expanded", "true");

        window.requestAnimationFrame(() => {
          const selectedIndex = this.sortOptions.findIndex(
            (option) => option.getAttribute("aria-selected") === "true",
          );
          const targetIndex =
            focusTarget === "last"
              ? this.sortOptions.length - 1
              : Math.max(0, selectedIndex);
          this.sortOptions[targetIndex]?.focus();
        });
      }

      closeSortMenu(options = {}) {
        if (!this.sortControl?.classList.contains("is-open")) return;
        this.sortControl.classList.remove("is-open");
        this.sortTrigger.setAttribute("aria-expanded", "false");
        if (options.focusTrigger) this.sortTrigger.focus();
      }

      chooseSortOption(option) {
        const value = option.dataset.blogSortOption;
        if (!this.isValidSort(value)) return;

        this.sortSelect.value = value;
        this.syncSortUi();
        this.closeSortMenu({ focusTrigger: true });
        this.sortSelect.dispatchEvent(new Event("change", { bubbles: true }));
      }

      syncSortUi() {
        if (!this.sortSelect || !this.sortOptions.length) return;
        const value = this.sortSelect.value || "newest";
        const selected = this.sortOptions.find(
          (option) => option.dataset.blogSortOption === value,
        );

        this.sortOptions.forEach((option) => {
          option.setAttribute(
            "aria-selected",
            String(option.dataset.blogSortOption === value),
          );
        });
        if (selected && this.sortValue) {
          this.sortValue.textContent =
            selected.querySelector("span")?.textContent?.trim() || "Newest";
        }
      }

      run(opts = {}) {
        const term = (this.input?.value || "").trim();
        const sort = this.sortSelect?.value || "newest";
        if (
          term === this.lastTerm &&
          sort === this.lastSort &&
          !opts.force
        ) {
          return;
        }
        this.lastTerm = term;
        this.lastSort = sort;

        this.toggleClear(term.length > 0);

        if (!opts.skipUrl) this.updateUrl(term, sort);

        if (!term && sort === "newest") {
          this.showDefaultView();
          return;
        }

        const tokens = term.toLowerCase().split(/\s+/).filter(Boolean);
        const matches = this.sortArticles(
          this.articles.filter((a) =>
            tokens.every((t) => a._haystack.includes(t)),
          ),
          sort,
        );

        this.renderResults(matches, term);
      }

      showDefaultView() {
        if (this.defaultView) this.defaultView.classList.remove("hidden");
        if (this.resultsWrap) this.resultsWrap.classList.add("hidden");
        if (this.pagination) {
          this.pagination.classList.add("hidden");
          this.pagination.innerHTML = "";
        }
        if (this.statusEl) {
          const defaultText = this.statusEl.dataset.defaultText || "";
          this.statusEl.textContent = defaultText;
        }
      }

      renderResults(matches, term) {
        if (this.defaultView) this.defaultView.classList.add("hidden");
        if (this.resultsWrap) this.resultsWrap.classList.remove("hidden");

        if (this.statusEl) {
          this.statusEl.textContent = this.formatCount(matches.length, term);
        }

        if (!this.resultsList) return; // empty tag page, nothing to render into

        if (matches.length === 0) {
          this.resultsList.innerHTML = "";
          if (this.emptyMsg) {
            this.emptyMsg.textContent = this.formatNoResults(term);
            this.emptyMsg.classList.remove("hidden");
          }
          this.renderPagination(0);
          return;
        }

        if (this.emptyMsg) this.emptyMsg.classList.add("hidden");

        const totalPages = Math.max(
          1,
          Math.ceil(matches.length / this.pageSize),
        );
        this.currentPage = Math.min(this.currentPage, totalPages);
        const start = (this.currentPage - 1) * this.pageSize;
        const pageMatches = matches.slice(start, start + this.pageSize);

        const frag = document.createDocumentFragment();
        pageMatches.forEach((article) => {
          frag.appendChild(this.buildCard(article, term));
        });
        this.resultsList.innerHTML = "";
        this.resultsList.appendChild(frag);
        this.renderPagination(totalPages);

        if (typeof webvista !== "undefined" && webvista.initLazyImages) {
          webvista.initLazyImages();
        }
      }

      isValidSort(value) {
        return ["newest", "oldest", "title-asc", "title-desc"].includes(
          value,
        );
      }

      sortArticles(articles, sort) {
        const sorted = [...articles];
        const compareTitles = (a, b) =>
          String(a.title || "").localeCompare(String(b.title || ""), undefined, {
            sensitivity: "base",
            numeric: true,
          });
        const compareDates = (a, b) =>
          (Number(a.published_at) || 0) - (Number(b.published_at) || 0);

        switch (sort) {
          case "oldest":
            sorted.sort((a, b) => compareDates(a, b) || compareTitles(a, b));
            break;
          case "title-asc":
            sorted.sort((a, b) => compareTitles(a, b) || -compareDates(a, b));
            break;
          case "title-desc":
            sorted.sort((a, b) => compareTitles(b, a) || -compareDates(a, b));
            break;
          case "newest":
          default:
            sorted.sort((a, b) => -compareDates(a, b) || compareTitles(a, b));
            break;
        }
        return sorted;
      }

      renderPagination(totalPages) {
        if (!this.pagination) return;

        this.pagination.innerHTML = "";
        if (totalPages <= 1) {
          this.pagination.classList.add("hidden");
          return;
        }

        const previous = this.buildPaginationButton(
          "Previous",
          this.currentPage - 1,
          this.currentPage === 1,
        );
        const status = document.createElement("span");
        status.className = "blog-client-pagination__status";
        status.textContent = `Page ${this.currentPage} of ${totalPages}`;
        status.setAttribute("aria-live", "polite");
        const next = this.buildPaginationButton(
          "Next",
          this.currentPage + 1,
          this.currentPage === totalPages,
        );

        this.pagination.append(previous, status, next);
        this.pagination.classList.remove("hidden");
      }

      buildPaginationButton(label, page, disabled) {
        const button = document.createElement("button");
        button.type = "button";
        button.className =
          "blog-client-pagination__button button button--small button--secondary";
        button.textContent = label;
        button.disabled = disabled;
        button.addEventListener("click", () => {
          this.currentPage = page;
          this.run({ force: true });
          this.statusEl?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return button;
      }

      buildCard(article, term) {
        const li = document.createElement("li");
        li.className = "grid-item focus-inset";
        li.tabIndex = 0;

        const wrap = document.createElement("div");
        wrap.className = "article-card-wrapper card-wrapper hover-trigger";

        const card = document.createElement("div");
        card.className = "card";

        // Media
        const inner = document.createElement("div");
        inner.className = "card-inner ratio";
        inner.style.setProperty("--ratio-percent", this.ratioPercent);
        const mediaWrap = document.createElement("div");
        mediaWrap.className = "card-media-wrapper";
        const media = document.createElement("div");
        media.className = "card-media media";

        if (article.image) {
          const img = document.createElement("img");
          img.loading = "lazy";
          img.decoding = "async";
          img.src = article.image;
          img.alt = article.image_alt || article.title || "";
          img.className = "animate-scale-up";
          media.appendChild(img);
        } else {
          const placeholder = document.createElement("div");
          placeholder.className = "placeholder-svg animate-scale-up";
          media.appendChild(placeholder);
        }

        mediaWrap.appendChild(media);
        inner.appendChild(mediaWrap);

        const mediaLink = document.createElement("a");
        mediaLink.href = article.url;
        mediaLink.className = "card-inner-content link";
        mediaLink.setAttribute("aria-label", article.title || "");
        mediaLink.tabIndex = -1;
        inner.appendChild(mediaLink);

        card.appendChild(inner);

        // Content
        const content = document.createElement("div");
        content.className = "card-content";

        const caption = document.createElement("div");
        caption.className = "card-caption caption";
        if (this.showDate && article.date_display) {
          const dateSpan = document.createElement("span");
          dateSpan.textContent = article.date_display;
          caption.appendChild(dateSpan);
        }
        if (this.showAuthor && article.author) {
          const authorSpan = document.createElement("span");
          authorSpan.textContent = article.author;
          caption.appendChild(authorSpan);
        }
        if (caption.childNodes.length) content.appendChild(caption);

        const heading = document.createElement("h3");
        heading.className = "card-heading font-body";
        const titleLink = document.createElement("a");
        titleLink.href = article.url;
        titleLink.className = "link link-underline";
        titleLink.innerHTML = this.highlight(article.title || "", term);
        heading.appendChild(titleLink);
        content.appendChild(heading);

        if (this.showExcerpt && article.excerpt) {
          const desc = document.createElement("div");
          desc.className = "card-description light";
          desc.innerHTML = this.highlight(
            this.truncate(article.excerpt, 25),
            term
          );
          content.appendChild(desc);
        }

        card.appendChild(content);
        wrap.appendChild(card);
        li.appendChild(wrap);
        return li;
      }

      highlight(text, term) {
        const escaped = this.escapeHtml(text);
        if (!term) return escaped;
        const tokens = term
          .split(/\s+/)
          .filter(Boolean)
          .map((t) => this.escapeRegExp(t));
        if (!tokens.length) return escaped;
        const re = new RegExp(`(${tokens.join("|")})`, "gi");
        return escaped.replace(re, "<mark>$1</mark>");
      }

      truncate(text, words) {
        const parts = text.split(/\s+/);
        if (parts.length <= words) return text;
        return parts.slice(0, words).join(" ") + "\u2026";
      }

      escapeHtml(s) {
        return String(s)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");
      }

      escapeRegExp(s) {
        return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      }

      computeRatio(ratio) {
        switch (ratio) {
          case "portrait":
            return "125%";
          case "square":
            return "100%";
          case "landscape":
          default:
            return "62.5%";
        }
      }

      toggleClear(visible) {
        if (!this.clearBtn) return;
        this.clearBtn.classList.toggle("hidden", !visible);
      }

      updateUrl(term, sort) {
        try {
          const url = new URL(window.location.href);
          if (term) {
            url.searchParams.set("bq", term);
          } else {
            url.searchParams.delete("bq");
          }
          if (sort && sort !== "newest") {
            url.searchParams.set("sort", sort);
          } else {
            url.searchParams.delete("sort");
          }
          if ((term || sort !== "newest") && this.currentPage > 1) {
            url.searchParams.set("bpage", String(this.currentPage));
          } else {
            url.searchParams.delete("bpage");
          }
          if (sort !== "newest") {
            url.searchParams.delete("page");
          }
          window.history.replaceState({}, "", url.toString());
        } catch (e) {
          /* ignore */
        }
      }

      formatCount(count, term) {
        if (!term) return `${count} articles found`;
        const tpl =
          count === 1
            ? this.dataset.i18nCountOne ||
              `1 result for \u201c__TERM__\u201d`
            : this.dataset.i18nCountOther ||
              `__COUNT__ results for \u201c__TERM__\u201d`;
        return tpl
          .replace(/__COUNT__/g, String(count))
          .replace(/__TERM__/g, term);
      }

      formatNoResults(term) {
        const tpl =
          this.dataset.i18nNoResults ||
          `No articles match \u201c__TERM__\u201d.`;
        return tpl.replace(/__TERM__/g, term);
      }
    }
  );
}
